/**
 * Scope 1 Emissions Detail Page
 *
 * Detailed view of direct emissions from owned or controlled sources.
 * Shows activity type breakdown, facilities, and individual activities.
 */

import { useState, useMemo, useCallback } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { PieChart, Pie, Cell, Label } from 'recharts';
import { ChartContainer, ChartTooltip, ChartTooltipContent } from '@g17eco/ui-modern/components/ui/chart';
import { ArrowRight, Flame, Calendar, TrendingUp, Loader2, Inbox } from 'lucide-react';
import { Button } from '@g17eco/ui-modern/components/ui/button';
import { SCOPE_TAILWIND } from '../config/scopeBranding';
import { ACTIVITY_TYPE_COLORS, ACTIVITY_TYPE_FA_ICONS, formatActivityType } from '../config/activityTypeBranding';
import { Dashboard } from '@g17eco/ui-modern/components/ui/dashboard';
import { Card, CardHeader, CardTitle, CardContent } from '@g17eco/ui-modern/components/ui/card';
import { cn } from '@g17eco/ui-modern/lib/utils';
import {
  useGetEmissionsSummaryQuery,
  useGetEmissionsByActivityTypeQuery,
  useTriggerCalculationMutation,
} from '../api/emissions';
import { generateToast, toastFromError } from '@g17eco/molecules/toasts';
import { GasBreakdownPanel } from '../components/GasBreakdownPanel';
import { AssetBreakdownPanel } from '../components/AssetBreakdownPanel';
import { emissionsTrackerRoutes, rootAppPath } from '../routes';
import { generateUrl, generateUrlWithQuery } from '@routes/util';
import { EmissionScope } from '../types/emissions';
import { EmissionsScopeNav } from '../components/EmissionsNav';
import { FiscalYearSelector, getFiscalYearPeriod, getPreviousYearPeriod } from '../components/FiscalYearSelector';
import { useAppSelector } from '@reducers/index';
import { TopEmissionSourcesCard } from '../components/TopEmissionSourcesCard';
import { RecentActivitiesCard } from '../components/RecentActivitiesCard';
import { CalculationDetailsDrawer } from '../components/CalculationDetailsDrawer';
import { IncludeChildrenToggle } from '../components/IncludeChildrenToggle';
import { useEmissionsFilters } from '../hooks/useEmissionsFilters';
import { useCalculationDrawer } from '../hooks/useCalculationDrawer';
import { getInitiativeTypeTree } from '../../../selectors/initiativeTree';
import { Badge } from '@g17eco/ui-modern/components/ui/badge';

const formatNumber = (value: number): string => {
  if (value >= 1000000) return `${(value / 1000000).toFixed(1)}M`;
  if (value >= 1000) return `${(value / 1000).toFixed(1)}k`;
  return value.toFixed(1);
};

export const EmissionsScope1Page = () => {
  const { initiativeId } = useParams<{ initiativeId: string }>();
  const history = useHistory();
  const {
    hasChildren,
    selectedFiscalYear,
    setSelectedFiscalYear,
    availableYears,
    includeChildren,
    setIncludeChildren,
  } = useEmissionsFilters();
  const initiativeTypeTree = useAppSelector(getInitiativeTypeTree);
  const initiativeLabelMap = useMemo(
    () => new Map(initiativeTypeTree.map((i) => [i._id, i.name])),
    [initiativeTypeTree],
  );
  const getReportingLevelLabel = useCallback(
    (activityInitiativeId: string) =>
      activityInitiativeId === initiativeId ? (
        <Badge variant='default'>Current Level</Badge>
      ) : (
        <Badge variant='outline'>{initiativeLabelMap.get(activityInitiativeId) ?? activityInitiativeId}</Badge>
      ),
    [initiativeId, initiativeLabelMap],
  );
  const [recalculatingActivityId, setRecalculatingActivityId] = useState<string | null>(null);
  const { selectedActivity, openDrawer, closeDrawer } = useCalculationDrawer();
  const [triggerCalculation] = useTriggerCalculationMutation();

  const handleRecalculate = useCallback(
    async (activityId: string, activityInitiativeId: string) => {
      setRecalculatingActivityId(activityId);
      try {
        await triggerCalculation({
          initiativeId: activityInitiativeId,
          activityId,
          options: { recalculate: true },
        }).unwrap();
        generateToast({ color: 'success', message: 'Emissions recalculated successfully' });
      } catch (error) {
        toastFromError({ error, title: 'Recalculation failed' });
      } finally {
        setRecalculatingActivityId(null);
      }
    },
    [triggerCalculation],
  );

  const periodDates = useMemo(() => getFiscalYearPeriod(selectedFiscalYear), [selectedFiscalYear]);
  const previousPeriodDates = useMemo(() => getPreviousYearPeriod(selectedFiscalYear), [selectedFiscalYear]);

  const { data: summary, isLoading: summaryLoading } = useGetEmissionsSummaryQuery(
    { initiativeId: initiativeId ?? '', periodStart: periodDates.start, periodEnd: periodDates.end, includeChildren },
    { skip: !initiativeId },
  );

  const { data: previousYearSummary, isLoading: previousYearLoading } = useGetEmissionsSummaryQuery(
    {
      initiativeId: initiativeId ?? '',
      periodStart: previousPeriodDates.start,
      periodEnd: previousPeriodDates.end,
      includeChildren,
    },
    { skip: !initiativeId },
  );

  const { data: activityTypeData, isLoading: activityTypeLoading } = useGetEmissionsByActivityTypeQuery(
    {
      initiativeId: initiativeId ?? '',
      periodStart: periodDates.start,
      periodEnd: periodDates.end,
      scopes: [EmissionScope.Scope1],
      includeChildren,
    },
    { skip: !initiativeId },
  );

  const scope1Total = summary?.byScope?.scope1 || 0;
  const totalEmissions = summary?.totalCO2e || 0;
  const scope1Percentage = totalEmissions > 0 ? Math.round((scope1Total / totalEmissions) * 100) : 0;

  const previousScope1Total = previousYearSummary?.byScope?.scope1 || 0;
  const yoyChange = useMemo(() => {
    if (previousScope1Total === 0) return null;
    return ((scope1Total - previousScope1Total) / previousScope1Total) * 100;
  }, [scope1Total, previousScope1Total]);

  const activityTypeBreakdown = useMemo(() => {
    if (!activityTypeData || activityTypeData.length === 0) return [];
    const total = activityTypeData.reduce((sum, item) => sum + item.total, 0);
    return activityTypeData
      .map((item) => {
        const color = ACTIVITY_TYPE_COLORS[item.activityType] || '#6c757d';
        const label = formatActivityType(item.activityType);
        return {
          source: label,
          icon: ACTIVITY_TYPE_FA_ICONS[item.activityType] ?? 'fa-leaf',
          percentage: total > 0 ? Math.round((item.total / total) * 100) : 0,
          color,
          emissions: Math.round(item.total / 1000),
        };
      })
      .sort((a, b) => b.emissions - a.emissions);
  }, [activityTypeData]);

  const handleGasClick = useCallback(
    (gas: 'co2' | 'ch4' | 'n2o') => {
      const url = generateUrlWithQuery(
        emissionsTrackerRoutes.EMISSIONS_ACTIVITIES,
        { rootAppPath, initiativeId },
        {
          scope: 'scope1',
          sort: gas,
          sortDir: 'desc',
          periodStart: periodDates.start,
          periodEnd: periodDates.end,
        },
      );
      history.push(url);
    },
    [initiativeId, periodDates, history],
  );

  return (
    <Dashboard>
      {/* Scope Sub-Navigation */}
      <EmissionsScopeNav />

      {/* Header */}
      <div className='tw:flex tw:justify-between tw:items-center tw:mb-6'>
        <div>
          <h4 className='tw:text-xl tw:font-semibold tw:mb-1 tw:flex tw:items-center tw:gap-2'>
            <Flame className={cn('tw:size-5', SCOPE_TAILWIND.scope1.icon)} />
            Scope 1 Emissions
          </h4>
          <p className='tw:text-sm tw:text-muted-foreground'>Direct emissions from owned or controlled sources</p>
        </div>
        <div className='tw:flex tw:gap-2'>
          <IncludeChildrenToggle
            disabled={!hasChildren}
            includeChildren={includeChildren}
            onChange={setIncludeChildren}
          />
          <FiscalYearSelector
            selectedYear={selectedFiscalYear}
            onYearChange={setSelectedFiscalYear}
            years={availableYears}
          />
        </div>
      </div>

      {/* Summary Cards Row */}
      <div className='tw:grid tw:grid-cols-1 tw:md:grid-cols-3 tw:gap-4 tw:mb-6'>
        {/* Total Scope 1 */}
        <Card className={cn('tw:border-l-[3px]', `tw:border-l-${SCOPE_TAILWIND.scope1.border}`)}>
          <CardContent className='tw:p-4'>
            <div className='tw:flex tw:justify-between tw:items-start'>
              <div>
                <p className='tw:text-sm tw:text-muted-foreground tw:mb-2'>Total Scope 1</p>
                {summaryLoading ? (
                  <Loader2 className='tw:size-5 tw:animate-spin tw:text-muted-foreground' />
                ) : (
                  <>
                    <div className='tw:flex tw:items-baseline tw:gap-1'>
                      <span className={cn('tw:text-2xl tw:font-bold', SCOPE_TAILWIND.scope1.icon)}>
                        {formatNumber(scope1Total)}
                      </span>
                      <span className='tw:text-sm tw:text-muted-foreground'>tCO₂e</span>
                    </div>
                    <div className='tw:text-xs tw:text-muted-foreground tw:mt-1'>
                      {scope1Percentage}% of total emissions
                    </div>
                  </>
                )}
              </div>
              <div
                className={cn(
                  'tw:rounded-xl tw:flex tw:items-center tw:justify-center tw:size-10',
                  SCOPE_TAILWIND.scope1.iconBg,
                )}
              >
                <Flame className={cn('tw:size-5', SCOPE_TAILWIND.scope1.icon)} />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Monthly Average */}
        <Card>
          <CardContent className='tw:p-4'>
            <div className='tw:flex tw:justify-between tw:items-start'>
              <div>
                <p className='tw:text-sm tw:text-muted-foreground tw:mb-2'>Monthly Average</p>
                {summaryLoading ? (
                  <Loader2 className='tw:size-5 tw:animate-spin tw:text-muted-foreground' />
                ) : (
                  <>
                    <div className='tw:flex tw:items-baseline tw:gap-1'>
                      <span className='tw:text-2xl tw:font-bold'>{formatNumber(scope1Total / 12)}</span>
                      <span className='tw:text-sm tw:text-muted-foreground'>tCO₂e</span>
                    </div>
                    <div className='tw:text-xs tw:text-muted-foreground tw:mt-1'>
                      per month in FY {selectedFiscalYear}
                    </div>
                  </>
                )}
              </div>
              <div className='tw:rounded-xl tw:flex tw:items-center tw:justify-center tw:size-10 tw:bg-blue-500/10'>
                <Calendar className='tw:size-5 tw:text-blue-600' />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* YoY Change */}
        <Card>
          <CardContent className='tw:p-4'>
            <div className='tw:flex tw:justify-between tw:items-start'>
              <div>
                <p className='tw:text-sm tw:text-muted-foreground tw:mb-2'>YoY Change</p>
                {summaryLoading || previousYearLoading ? (
                  <Loader2 className='tw:size-5 tw:animate-spin tw:text-muted-foreground' />
                ) : yoyChange === null ? (
                  <>
                    <div className='tw:text-2xl tw:font-bold tw:text-muted-foreground'>N/A</div>
                    <div className='tw:text-xs tw:text-muted-foreground tw:mt-1'>No previous year data</div>
                  </>
                ) : (
                  <>
                    <div
                      className={cn(
                        'tw:text-2xl tw:font-bold',
                        yoyChange <= 0 ? 'tw:text-emerald-600' : 'tw:text-red-600',
                      )}
                    >
                      {yoyChange > 0 ? '+' : ''}
                      {yoyChange.toFixed(1)}%
                    </div>
                    <div
                      className={cn('tw:text-xs tw:mt-1', yoyChange <= 0 ? 'tw:text-emerald-600' : 'tw:text-red-600')}
                    >
                      {yoyChange <= 0 ? '↓ Reduced' : '↑ Increased'} vs last year
                    </div>
                  </>
                )}
              </div>
              <div
                className={cn(
                  'tw:rounded-xl tw:flex tw:items-center tw:justify-center tw:size-10',
                  yoyChange === null ? 'tw:bg-muted' : yoyChange <= 0 ? 'tw:bg-emerald-500/10' : 'tw:bg-red-500/10',
                )}
              >
                <TrendingUp
                  className={cn(
                    'tw:size-5',
                    yoyChange === null
                      ? 'tw:text-muted-foreground'
                      : yoyChange <= 0
                        ? 'tw:text-emerald-600'
                        : 'tw:text-red-600',
                  )}
                />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Breakdowns */}
      <div className='tw:grid tw:grid-cols-1 tw:lg:grid-cols-2 tw:gap-6 tw:mb-6'>
        <GasBreakdownPanel byGas={summary?.byScopeGas?.scope1} isLoading={summaryLoading} onGasClick={handleGasClick} />

        <Card className='tw:h-full'>
          <CardHeader>
            <div className='tw:flex tw:justify-between tw:items-center tw:gap-3'>
              <CardTitle>Activity Type Breakdown</CardTitle>
              <Button
                variant='link'
                size='sm'
                onClick={() => history.push(generateUrlWithQuery(
                  emissionsTrackerRoutes.EMISSIONS_INSIGHTS,
                  { rootAppPath, initiativeId },
                  { tab: 'sources' },
                ))}
              >
                View all
                <ArrowRight />
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {activityTypeLoading ? (
              <div className='tw:flex tw:items-center tw:justify-center' style={{ height: 220 }}>
                <Loader2 className='tw:size-6 tw:animate-spin tw:text-muted-foreground' />
              </div>
            ) : activityTypeBreakdown.length === 0 ? (
              <div
                className='tw:flex tw:flex-col tw:items-center tw:justify-center tw:text-muted-foreground'
                style={{ height: 220 }}
              >
                <Inbox className='tw:size-10 tw:mb-2 tw:opacity-50' />
                <p className='tw:text-sm'>No activity data</p>
              </div>
            ) : (
              <div className='tw:flex tw:items-center' style={{ height: 220 }}>
                <div className='tw:flex-1 tw:min-w-0 tw:h-full'>
                  <ChartContainer config={{}} className='tw:w-full tw:h-full'>
                    <PieChart>
                      <ChartTooltip content={<ChartTooltipContent hideLabel />} />
                      <Pie
                        data={activityTypeBreakdown.map((d) => ({
                          name: d.source,
                          emissions: d.emissions,
                          fill: d.color,
                        }))}
                        dataKey='emissions'
                        nameKey='name'
                        innerRadius={65}
                        outerRadius={90}
                        strokeWidth={2}
                      >
                        {activityTypeBreakdown.map((item) => (
                          <Cell key={item.source} fill={item.color} />
                        ))}
                        <Label
                          content={({ viewBox }) => {
                            if (viewBox && 'cx' in viewBox && 'cy' in viewBox) {
                              return (
                                <text x={viewBox.cx} y={viewBox.cy} textAnchor='middle' dominantBaseline='middle'>
                                  <tspan
                                    x={viewBox.cx}
                                    y={viewBox.cy}
                                    className='tw:fill-muted-foreground tw:text-xs tw:font-medium'
                                  >
                                    By Type
                                  </tspan>
                                </text>
                              );
                            }
                            return null;
                          }}
                        />
                      </Pie>
                    </PieChart>
                  </ChartContainer>
                </div>
                <div className='tw:w-[45%] tw:shrink-0 tw:pl-4'>
                  {activityTypeBreakdown.map((item) => (
                    <div key={item.source} className='tw:flex tw:items-center tw:justify-between tw:mb-2'>
                      <div className='tw:flex tw:items-center tw:gap-1.5'>
                        <div
                          className='tw:rounded tw:flex tw:items-center tw:justify-center tw:shrink-0'
                          style={{ width: 20, height: 20, backgroundColor: `${item.color}22` }}
                        >
                          <i className={`fa ${item.icon}`} style={{ fontSize: '0.6rem', color: item.color }} />
                        </div>
                        <span className='tw:text-xs'>{item.source}</span>
                      </div>
                      <span className='tw:text-xs tw:font-medium'>{item.percentage}%</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Asset Breakdown (Scope 1 filtered) */}
      <div className='tw:mb-6'>
        <AssetBreakdownPanel
          initiativeId={initiativeId}
          periodStart={periodDates.start}
          periodEnd={periodDates.end}
          includeChildren={includeChildren}
          scope={EmissionScope.Scope1}
          variant='cards'
          onAssetSelect={(assetId) => {
            history.push(
              generateUrl(emissionsTrackerRoutes.EMISSIONS_ASSET_DASHBOARD, {
                rootAppPath,
                initiativeId,
                assetId,
              }),
            );
          }}
        />
      </div>

      {/* Top Sources + Recent Activities summary row */}
      <div className='tw:grid tw:grid-cols-1 tw:lg:grid-cols-2 tw:gap-6 tw:mb-6'>
        <TopEmissionSourcesCard
          initiativeId={initiativeId}
          periodStart={periodDates.start}
          periodEnd={periodDates.end}
          scope={EmissionScope.Scope1}
          includeChildren={includeChildren}
        />
        <RecentActivitiesCard
          initiativeId={initiativeId}
          scope={EmissionScope.Scope1}
          periodStart={periodDates.start}
          periodEnd={periodDates.end}
          includeChildren={includeChildren}
          title={(view) => `${view === 'top' ? 'Top' : 'Latest'} 5 Scope 1 Activities`}
          viewAllUrl={(view) =>
            generateUrlWithQuery(
              emissionsTrackerRoutes.EMISSIONS_ACTIVITIES,
              { rootAppPath, initiativeId },
              view === 'top'
                ? { scope: 'scope1', sort: 'calculatedCO2e', sortDir: 'desc' }
                : { scope: 'scope1' },
            )
          }
          onViewCalculation={openDrawer}
          emptyAction={{
            label: 'Add Scope 1 activity',
            to: generateUrl(emissionsTrackerRoutes.EMISSIONS_ACTIVITY_NEW, { rootAppPath, initiativeId }),
          }}
        />
      </div>

      <CalculationDetailsDrawer
        isOpen={selectedActivity !== null}
        toggle={closeDrawer}
        initiativeId={selectedActivity?.initiativeId ?? initiativeId}
        activityId={selectedActivity?._id ?? null}
        onRecalculate={handleRecalculate}
        isRecalculating={recalculatingActivityId !== null}
        activityData={
          selectedActivity
            ? {
                activityType: selectedActivity.activityType,
                bulkImportId: selectedActivity.bulkImportId,
                quantity: selectedActivity.quantity,
                unit: selectedActivity.unit,
                region: selectedActivity.region,
                periodStart: selectedActivity.periodStart,
                periodEnd: selectedActivity.periodEnd,
                scope: selectedActivity.scope,
                fuelType: selectedActivity.fuelType,
                vehicleType: selectedActivity.vehicleType,
                refrigerantType: selectedActivity.refrigerantType,
                processType: selectedActivity.processType,
                regionFallback: selectedActivity.regionFallback,
                yearFallback: selectedActivity.yearFallback,
                calculationNotices: selectedActivity.calculationNotices,
              }
            : undefined
        }
        reportingLevelLabel={selectedActivity ? getReportingLevelLabel(selectedActivity.initiativeId) : undefined}
      />
    </Dashboard>
  );
};
